import { useTasksMutation } from 'app/data/tasks/useTasksMutation'
import { Task, TaskFormData } from 'app/data/types/tasks'
import { useCallback, useEffect } from 'react'
import { useTaskDialog } from './useTaskDialog'

const logTaskActions = (_message: string, _data?: any) => {
  //console.log(`[useTaskActions] ${message}`, data || '')
}

/**
 * Hook that combines task mutations with dialog management.
 * Handles all task-related actions (create, update, delete) and manages dialog state.
 */
export function useTaskActions() {
  const dialog = useTaskDialog()
  const mutations = useTasksMutation()

  // Update logging to use visible instead of isOpen
  useEffect(() => {
    logTaskActions('dialog state changed', {
      visible: dialog.visible,
      hasEditingTask: !!dialog.editingTask,
      defaultFocusDate: dialog.defaultFocusDate,
    })
  }, [dialog.visible, dialog.editingTask, dialog.defaultFocusDate])

  // Add logging for mutations state
  useEffect(() => {
    logTaskActions('mutations state changed', {
      isLoading: mutations.isLoading,
    })
  }, [mutations.isLoading])

  const { createTask, updateTask, deleteTask, updateTaskStatus, updateStepStatus } = mutations

  /**
   * Handles form submission for both creating and editing tasks.
   * 
   * CRITICAL: This method contains the fix for the original bug where tasks were
   * trying to PUT to /api/v1/tasks/undefined instead of POST to /api/v1/tasks.
   * 
   * The bug occurred because:
   * 1. User clicks existing task → editingTask gets set in dialog store
   * 2. User clicks "+" button → dialog opens but editingTask is still set
   * 3. Form thinks it's editing (editingTask exists) but task.id is undefined
   * 4. Results in PUT /api/v1/tasks/undefined instead of POST /api/v1/tasks
   * 
   * The fix: Check for BOTH editingTask AND editingTask.id before deciding to update vs create.
   */
  const handleTaskSubmit = useCallback(
    async (formData: TaskFormData): Promise<Task> => {
      logTaskActions('submitting task', { hasEditingTask: !!dialog.editingTask, taskId: dialog.editingTask?.id })

      try {
        let result: Task
        
        // THE CRITICAL FIX: Check for both editingTask AND a valid ID
        if (dialog.editingTask?.id) {
          // EDIT PATH: We have a valid existing task with an ID
          // This will result in: PUT /api/v1/tasks/{id}
          logTaskActions('taking EDIT path', { taskId: dialog.editingTask.id })
          const { steps, initialSteps, ...taskData } = formData
          result = await updateTask({
            taskId: dialog.editingTask.id,
            taskData,
            initialStepData: initialSteps,
            stepData: steps,
          })
        } else {
          // CREATE PATH: Either no editingTask OR editingTask has no valid ID
          // This will result in: POST /api/v1/tasks
          logTaskActions('taking CREATE path', { reason: !dialog.editingTask ? 'no editingTask' : 'no valid ID' })
          const { steps, initialSteps, ...taskData } = formData
          result = await createTask({
            taskData,
            steps: steps || [],
          })
        }
        
        dialog.hide()
        return result
      } catch (error) {
        console.error('Failed to submit task:', error)
        throw error
      }
    },
    [dialog, createTask, updateTask]
  )

  const handleTaskDelete = useCallback(
    async (taskId: string): Promise<void> => {
      logTaskActions('deleting task', { taskId })
      try {
        await deleteTask(taskId)
        dialog.hide()
      } catch (error) {
        console.error('Failed to delete task:', error)
        throw error
      }
    },
    [dialog, deleteTask]
  )

  const handleTaskStatusChange = useCallback(
    async (taskId: string, completed: boolean): Promise<void> => {
      logTaskActions('updating task status', { taskId, completed })
      try {
        await updateTaskStatus({
          taskId,
          status: completed ? 'completed' : 'incomplete',
        })
      } catch (error) {
        console.error('Failed to update task status:', error)
        throw error
      }
    },
    [updateTaskStatus]
  )

  const handleStepStatusChange = useCallback(
    async (taskId: string, stepId: string, completed: boolean): Promise<void> => {
      logTaskActions('updating step status', { taskId, stepId, completed })
      try {
        await updateStepStatus({
          taskId,
          stepId,
          status: completed ? 'completed' : 'incomplete',
        })
      } catch (error) {
        console.error('Failed to update step status:', error)
        throw error
      }
    },
    [updateStepStatus]
  )

  /**
   * Opens the task dialog to CREATE a new task.
   * This should ONLY be called when the user clicks the "+" button or similar create action.
   * 
   * @param defaultDate - Optional date to pre-fill for the new task
   */
  const openCreateDialog = useCallback(
    (defaultDate?: string) => {
      logTaskActions('opening CREATE dialog', { defaultDate })
      // Explicitly pass undefined as task to ensure we're creating, not editing
      dialog.show(undefined, defaultDate)
    },
    [dialog]
  )

  /**
   * Opens the task dialog to EDIT an existing task.
   * This should ONLY be called when the user clicks on an existing task.
   * 
   * @param task - The existing task to edit (must have a valid ID)
   * @param contextDate - Optional date context (e.g., when editing from homepage's selected date)
   */
  const openEditDialog = useCallback(
    (task: Task, contextDate?: string) => {
      logTaskActions('opening EDIT dialog', { taskId: task.id, contextDate })
      // When editing from homepage, contextDate provides important date context
      // that might be needed if the task's focus_date is in an unexpected format
      dialog.show(task, contextDate)
    },
    [dialog]
  )

  return {
    // Dialog state
    isDialogOpen: dialog.visible,
    editingTask: dialog.editingTask,
    defaultFocusDate: dialog.defaultFocusDate,

    // Dialog actions
    openCreateDialog, // ONLY for creating new tasks (+ button)
    openEditDialog,   // ONLY for editing existing tasks (click task)
    closeDialog: dialog.hide,

    // Task operations
    handleTaskSubmit,
    handleTaskDelete,
    handleTaskStatusChange,
    handleStepStatusChange,
  }
}
